import * as React from 'react'

import { cn } from './cn'

export interface CircularProgressProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'color'> {
  /** Diameter in px (or CSS size string). */
  size?: number | string
  color?: 'primary' | 'secondary' | 'inherit' | 'error' | 'warning' | 'success'
  thickness?: number
  variant?: 'indeterminate' | 'determinate'
  /** Percent complete for determinate variant (0-100). */
  value?: number
}

const colorClasses = {
  primary: 'text-primary',
  secondary: 'text-primary',
  inherit: 'text-current',
  error: 'text-error',
  warning: 'text-warning',
  success: 'text-success',
}

const CircularProgress = React.forwardRef<
  HTMLSpanElement,
  CircularProgressProps
>(
  (
    {
      size = 40,
      color = 'primary',
      thickness = 3.6,
      variant = 'indeterminate',
      value = 0,
      className,
      style,
      ...props
    },
    ref,
  ) => {
    const dimension = typeof size === 'number' ? `${size}px` : size
    const determinate = variant === 'determinate'
    // circle r=20.2 -> circumference ~127
    const circumference = 2 * Math.PI * 20.2
    return (
      <span
        ref={ref}
        role="progressbar"
        aria-valuenow={determinate ? Math.round(value) : undefined}
        className={cn(
          'inline-block',
          !determinate && 'animate-spin',
          determinate && '-rotate-90',
          colorClasses[color],
          className,
        )}
        style={{
          width: dimension,
          height: dimension,
          animationDuration: '0.8s',
          ...style,
        }}
        {...props}
      >
        <svg viewBox="22 22 44 44" className="block h-full w-full">
          <circle
            cx="44"
            cy="44"
            r="20.2"
            fill="none"
            stroke="currentColor"
            strokeWidth={thickness}
            strokeDasharray={
              determinate
                ? `${(value / 100) * circumference}px ${circumference}px`
                : '80px 200px'
            }
            strokeDashoffset="0"
            strokeLinecap="round"
          />
        </svg>
      </span>
    )
  },
)
CircularProgress.displayName = 'CircularProgress'

export default CircularProgress
